import { Box, Drawer, Typography } from '@material-ui/core'
import { TaxationSchemeSettings } from 'components/domain/taxation/TaxationSchemeSettings'
import { CustomNavBar } from 'components/facets/CustomNavBar/CustomNavBar'
import { useUser } from 'components/providers/UserProvider'
import { useAppContext } from 'lib/hooks'
import { useMobileSidebarStore } from 'lib/hooks/useMobileSidebarStore'
import { isFromMainHost } from 'lib/utils'
import { useSnackbar } from 'notistack'
import qs from 'query-string'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { v4 as uuid } from 'uuid'
import './account-presenter.css'
import { AccountSidebar } from './AccountSidebar'
import { ApiKeysPresenter } from './ApiKeysPresenter'
import { AppSettingsPresenter } from './AppSettingsPresenter'
import { OrdersSettingsPresenter } from './OrdersSettingsPresenter'
import { SalesChannelsPresenter } from './SalesChannelsPresenter'
import { SepaMandatePresenter } from './SepaMandatePresenter'
import { SubscriptionPresenter } from './SubscriptionPresenter'
import { TutorialsPresenter } from './TutorialsPresenter'

export type OutsetaTab =
  | 'profile'
  | 'billing'
  | 'sepaMandate'
  | 'appSettings'
  | 'ordersSettings'
  | 'tutorials'
  | 'plan'
  | 'taxation'
  | 'apiKeys'
export type SidebarTab = 'account' | 'sales-channels'

export const AccountPresenter = (): JSX.Element => {
  const { t } = useTranslation()
  const { user, fetchUser } = useUser()
  const { isMobile } = useAppContext()
  const { enqueueSnackbar } = useSnackbar()
  const mobileSidebar = useMobileSidebarStore()
  const [activeAccountTab, setActiveAccountTab] = useState<string>('profile')
  const profileRef = useRef<HTMLDivElement>(null)
  const { search } = useLocation()
  const [accountTabs, setAccountTabs] = useState<any[]>([])
  const [activeSidebarTab, setActiveSidebarTab] = useState<SidebarTab>('account')
  const [sidebarTab, setSidebarTab] = useState<any[]>([])

  if (!user) return <></>

  const query = qs.parse(search)

  //@ts-ignore
  window.o_options = {
    id: 'Outseta',
    domain: 'mtg-powertools.outseta.com',
    load: 'profile',
    tokenStorage: 'local',
    profile: {
      id: `profile_embed-${uuid()}`,
      mode: 'embed',
      tab: activeAccountTab,
      selector: '#profile-embed',
    },
  }

  const changeAccountTab = (tab: OutsetaTab): void => {
    //@ts-ignore
    window?.Outseta?.profile?.setTab(tab)
    setActiveAccountTab(tab)
  }

  const changeSidebarTab = (tab: SidebarTab): void => {
    if (tab === 'account') setActiveAccountTab('profile')
    setActiveSidebarTab(tab)
    if (isMobile) mobileSidebar.setAccount(false)
  }

  useEffect(() => {
    if (profileRef.current) {
      const script = document.createElement('script')
      script.src = 'https://cdn.outseta.com/outseta.min.js'
      //@ts-ignore
      script['data-o-options'] = 'o_options'
      profileRef.current.appendChild(script)

      return () => {
        //@ts-ignore
        if (window.Outseta) window.Outseta = null
        if (profileRef.current?.contains(script)) {
          profileRef.current.removeChild(script)
        }
      }
    }
  }, [profileRef.current, activeAccountTab])

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null
    if (activeAccountTab === 'billing') {
      interval = setInterval(fetchUser, 3000)
    }

    return () => {
      if (interval) clearInterval(interval)
      return
    }
  }, [activeAccountTab])

  useEffect(() => {
    if (query.success) {
      changeAccountTab('sepaMandate')
    }
    if (query.tab === 'salesChannels') {
      changeSidebarTab('sales-channels')
    }

    const newSidebarTabs = []
    newSidebarTabs.push({
      tab: 'account',
      transKey: 'account',
      disabled: false,
      disabledTooltip: null,
      dataTestid: 'account-tab',
    })
    newSidebarTabs.push({
      tab: 'salesChannels',
      transKey: 'channels',
      disabled: false,
      disabledTooltip: null,
      dataTestid: 'sales-channels-tab',
    })

    setSidebarTab(newSidebarTabs)

    const newTabs = []
    newTabs.push({
      tab: 'profile',
      transKey: isFromMainHost() ? 'profile' : 'profileAlt', //TODO: Shopify Review fixes, should be removed
      disabled: false,
      disabledTooltip: null,
      dataTestid: 'profile-tab',
    })
    //TODO: Shopify Review fixes, should be removed
    if (isFromMainHost()) {
      newTabs.push({
        tab: 'billing',
        transKey: 'creditCard',
        disabled: !!user.stripePaymentMethod,
        disabledTooltip: 'removeMandateFirst',
        dataTestid: 'add-credit-card-tab',
      })
      newTabs.push({
        tab: 'sepaMandate',
        transKey: 'sepaMandate.title',
        disabled: !!user.outseta?.hasCreditCard,
        disabledTooltip: 'removeCardFirst',
        dataTestid: 'sepa-mandate-tab',
      })
    }
    newTabs.push({
      tab: 'appSettings',
      transKey: 'appSettings',
      disabled: false,
      disabledTooltip: null,
      dataTestid: 'app-settings-tab',
    })
    newTabs.push({
      tab: 'ordersSettings',
      transKey: 'ordersSettings',
      disabled: false,
      disabledTooltip: null,
      dataTestid: 'orders-settings-tab',
    })
    newTabs.push({
      tab: 'tutorials',
      transKey: 'tutorials',
      disabled: false,
      disabledTooltip: null,
      dataTestid: 'tutorials-tab',
    })

    if (user.featureFlags?.taxation && user.enableMarginTaxation) {
      newTabs.push({
        tab: 'taxation',
        transKey: 'taxation',
        disabled: false,
        disabledTooltip: null,
        dataTestid: 'taxation-tab',
      })
    }

    if (user.featureFlags?.api) {
      newTabs.push({
        tab: 'apiKeys',
        transKey: 'apiKeys.title',
        disabled: false,
        disabledTooltip: null,
        dataTestid: 'api-keys-tab',
      })
    }

    setAccountTabs(newTabs)

    if (query.tab === 'taxation') {
      setActiveAccountTab('taxation')
    }

    if (query.marginEnabled === 'true') {
      enqueueSnackbar(t('taxation.settings.marginEnabledSuccess'), { variant: 'success' })
    }
  }, [])

  const subscriptionSection = () => {
    if (!isFromMainHost()) return <></> //TODO: Shopify Review fixes, should be removed

    return (
      <>
        <h4 className="ps-3 my-4">Plan</h4>
        <SubscriptionPresenter subscription={user.subscription!} />
      </>
    )
  }

  const returnAccountContentAccordingAccountSelectedTab = (activeTab: string) => {
    switch (activeTab) {
      case 'profile':
        return (
          <div id="plan-and-profile" className="d-flex flex-column align-items-start pb-5">
            <h4 className="ps-3">Profile</h4>
            <Box style={{ position: 'relative', bottom: '0px' }}>
              <Box
                style={{
                  display: ['profile'].includes(activeTab) ? 'initial' : 'none',
                }}
              >
                <div
                  id="profile-embed"
                  className="state-profileView"
                  ref={profileRef}
                  style={{ float: 'left' }}
                ></div>
              </Box>
            </Box>
            {subscriptionSection()}
          </div>
        )

      case 'billing':
        return (
          <div>
            <h4 className="ps-3">Add credit card</h4>
            <Box style={{ position: 'relative', bottom: '0px' }}>
              <Box
                style={{
                  display: ['billing'].includes(activeTab) ? 'initial' : 'none',
                }}
              >
                <div
                  id="profile-embed"
                  className="state-profileView"
                  ref={profileRef}
                  style={{ float: 'left' }}
                ></div>
              </Box>
            </Box>
          </div>
        )

      case 'sepaMandate':
        return (
          <div className="p-4">
            <SepaMandatePresenter />
          </div>
        )

      case 'appSettings':
        return (
          <div className="p-4">
            <AppSettingsPresenter />
          </div>
        )

      case 'ordersSettings':
        return (
          <div className="p-4">
            <OrdersSettingsPresenter />
          </div>
        )

      case 'tutorials':
        return (
          <div className="p-4">
            <TutorialsPresenter />
          </div>
        )

      case 'taxation':
        return (
          <div className="p-4">
            <TaxationSchemeSettings />
          </div>
        )

      case 'apiKeys':
        return (
          <div className="p-4">
            <ApiKeysPresenter />
          </div>
        )
    }
  }

  const returnPageContentAccordingSelectedSidebarTab = (sidebarTab: string) => {
    if (sidebarTab === 'account')
      return (
        <div
          id="my-account-section"
          className={`w-100 h-100 d-flex flex-column justify-content-start mt-3`}
        >
          <CustomNavBar
            tabs={accountTabs.map((tab) => ({
              key: tab.tab,
              label: t(`account.${tab.transKey}`),
              dataTestid: tab.dataTestid,
            }))}
            activeTab={activeAccountTab}
            onTabChange={(key) => changeAccountTab(key as OutsetaTab)}
          />
          <div className="tab-page-content">
            <div className="pb-3 ps-3">
              {!user.outseta?.accessToken && (
                <>
                  <Typography variant="body1">{t('account.changeSettingsUnavailable')}</Typography>
                </>
              )}
            </div>
            <div>{returnAccountContentAccordingAccountSelectedTab(activeAccountTab)}</div>
          </div>
        </div>
      )
    else return <SalesChannelsPresenter />
  }

  return (
    <div className="d-flex flex-row">
      {!isMobile && (
        <div
          className="d-flex justify-content-center align-items-start"
          style={{ marginRight: 16 }}
        >
          <AccountSidebar
            onTabChange={changeSidebarTab}
            tabs={sidebarTab}
            activeTab={activeSidebarTab}
          />
        </div>
      )}
      {isMobile && (
        <Drawer
          anchor="left"
          open={mobileSidebar.account}
          onClose={() => mobileSidebar.setAccount(false)}
        >
          <AccountSidebar
            onTabChange={changeSidebarTab}
            tabs={sidebarTab}
            activeTab={activeSidebarTab}
          />
        </Drawer>
      )}
      {returnPageContentAccordingSelectedSidebarTab(activeSidebarTab)}
    </div>
  )
}
